import { useState } from "react";
import { isMobile } from "react-device-detect";
import styled from "styled-components";
import { ProSubscriptionType } from "@lifeat/shared/stripe/StripeSubscriptionTypes";
import { hasFreeAppAccess } from "@lifeat/shared/user/UserUtil";
import { LifeAtBodySecondary } from "@common/LifeAtText/LifeAtText";
import LifeAtToggle from "@common/LifeAtToggle/LifeAtToggle";
import { UpgradeCtaContextType } from "@contexts/CallToActionContext";
import { useUserContext } from "@contexts/UserContext";
import StudentDiscountCode from "@features/upgrade-flow/components/StudentDiscountCode";
import { Size } from "@themes/Device";
import { FontSize } from "@themes/Fonts";
import { AdditionalSourceData } from "@user/AmplitudeProSubsEvents";
import { UPGRADE_CONTENT } from "../PricingConstants";
import { FreeTier, ProTier } from "./PricingTiers";

export const PricingPageContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 15px;
    padding: 40px 30px;
`;

export const PricingTiersContainer = styled.div`
    display: flex;
    gap: 15px;

    @media (max-width: ${Size.tabletS}) {
        flex-direction: column;
        align-items: center;
    }
`;

export const PricingPageHeader = styled.span`
    color: ${(props) => props.theme.textColorPrimary};
    font-size: ${FontSize.xxl};
    max-width: 400px;
    text-align: center;
`;

export const PricingToggleContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    gap: 7px;
`;

const IframeContainer = styled.div`
    width: 100%;
    height: auto;
`;

const Iframe = styled.iframe`
    width: 100%;
    height: 3800px;
    border: none;
`;

const PRICING_TOGGLE_LABEL_MONTHLY = "Monthly";
const PRICING_TOGGLE_LABEL_YEARLY = "Yearly (Save 15%)";
const TESTIMONIAL_IFRAME_LINK = "https://www.lifeat.com/pro-upgrade-testimonials";

interface PricingPageContentProps {
    readonly upgradeContext: UpgradeCtaContextType;
    readonly hideFreeTier?: boolean;
    readonly hideTestimonials?: boolean;
    readonly amplitudeEventAdditionalSourceData?: AdditionalSourceData;
    readonly className?: string;
}

export default function PricingPageContent(props: PricingPageContentProps) {
    const { userData } = useUserContext();
    const isAppBlocked = !hasFreeAppAccess(userData);
    const [subscriptionPeriod, setSubscriptionPeriod] = useState<ProSubscriptionType>("annual");
    const handlePeriodToggle = () => {
        if (subscriptionPeriod === "monthly") {
            setSubscriptionPeriod("annual");
        } else if (subscriptionPeriod === "annual") {
            setSubscriptionPeriod("monthly");
        }
    };

    return (
        <PricingPageContentContainer className={props.className}>
            {!isAppBlocked && <StudentDiscountCode />}
            <PricingPageHeader>{UPGRADE_CONTENT[props.upgradeContext].header}</PricingPageHeader>
            <PricingToggleContainer>
                <LifeAtBodySecondary>{PRICING_TOGGLE_LABEL_MONTHLY}</LifeAtBodySecondary>
                <LifeAtToggle isEnabled={subscriptionPeriod === "annual"} onChange={handlePeriodToggle} />
                <LifeAtBodySecondary>{PRICING_TOGGLE_LABEL_YEARLY}</LifeAtBodySecondary>
            </PricingToggleContainer>
            <PricingTiersContainer>
                {!isMobile && !props.hideFreeTier && <FreeTier />}
                <ProTier
                    subscriptionPeriod={subscriptionPeriod}
                    amplitudeEventSource={props.upgradeContext}
                    amplitudeEventAdditionalSourceData={props.amplitudeEventAdditionalSourceData}
                />
            </PricingTiersContainer>
            {!props.hideTestimonials && (
                <IframeContainer>
                    <Iframe src={TESTIMONIAL_IFRAME_LINK} scrolling={"no"} />
                </IframeContainer>
            )}
        </PricingPageContentContainer>
    );
}
